'use client'
import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import InlineLinkList from '@/components/common/InlineLinkList'
import { consumerRequest } from '@/helper/request'
import { POPULAR_PRODUCT_PRICES_API } from '@/helper/apiConstants'
import {
  getServiceableCitySlugFromPathname,
  getTitleCaseNameBySlug,
  getNonCityProductUrl,
  getProductUrl,
} from '@/utils/commonFunc'
import { DEFAULT_CITY_SLUG } from '@/utils/constants'

interface PopularTestLinksProps {
  id?: string
  heading: string
  fetchedCitySlug: string
  products: any[]
  serviceableCitySlugs: string[]
}

// Unlike the other footer sections this list is city-specific, so a city switch must refetch: reusing the old city's products would build links to tests that city may not offer.
const PopularTestLinks = ({
  id,
  heading,
  fetchedCitySlug,
  products,
  serviceableCitySlugs,
}: PopularTestLinksProps) => {
  const routeCitySlug =
    getServiceableCitySlugFromPathname(
      usePathname() || '',
      serviceableCitySlugs
    ) || undefined
  const targetCitySlug = routeCitySlug || DEFAULT_CITY_SLUG
  const [resolved, setResolved] = useState(products)

  useEffect(() => {
    if (targetCitySlug === fetchedCitySlug) {
      setResolved(products)
      return
    }

    let cancelled = false
    consumerRequest(POPULAR_PRODUCT_PRICES_API, 'post', {
      citySlug: targetCitySlug,
    }).then((apiResponse: any) => {
      if (cancelled) return
      if (!apiResponse?.status) {
        console.error(
          'Failed to refetch popular product prices after city change',
          apiResponse?.message
        )
        return
      }
      setResolved(Array.isArray(apiResponse?.data) ? apiResponse.data : [])
    })

    return () => {
      cancelled = true
    }
  }, [targetCitySlug, fetchedCitySlug, products])

  const cityName = routeCitySlug
    ? getTitleCaseNameBySlug(routeCitySlug)
    : undefined
  const items = resolved.map((product: any) => ({
    label:
      routeCitySlug && cityName
        ? `${product?.name} in ${cityName}`
        : `${product?.name}`,
    href: routeCitySlug
      ? getProductUrl(product, routeCitySlug)
      : getNonCityProductUrl(product),
  }))

  return <InlineLinkList id={id} heading={heading} items={items} />
}

export default PopularTestLinks
